import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { accessRequest } from "@/api/client-access";
import { Button } from "@/components/ui/button";

type Account = {
  id: number;
  username: string;
  full_name: string;
  client_id: string;
  role: string;
  is_active: boolean;
  must_change_password: boolean;
};
const field =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm";

export function ClientAccess() {
  const { user, clients, accessEnabled } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [resetId, setResetId] = useState<number | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let live = true;
    if (user?.is_superuser && accessEnabled)
      accessRequest("/api/access/users")
        .then((data) => {
          if (live) setAccounts(data.users);
        })
        .catch((e) => {
          if (live) setError(e.message);
        });
    return () => {
      live = false;
    };
  }, [user?.is_superuser, accessEnabled, revision]);
  if (!user?.is_superuser || !accessEnabled)
    return <Navigate to="/loop" replace />;

  async function submit(
    event: React.FormEvent<HTMLFormElement>,
    path: string,
    method: string,
    body: (data: FormData) => unknown,
    success: string,
  ) {
    event.preventDefault();
    const form = event.currentTarget;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await accessRequest(path, method, body(new FormData(form)));
      form.reset();
      setEditing(null);
      setResetId(null);
      setRevision((r) => r + 1);
      setMessage(success);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const clientOptions = clients.map((c) => (
    <option key={c.id} value={c.id}>
      {c.name}
    </option>
  ));
  return (
    <div className="max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Clients & access</h1>
        <p className="mt-2 text-muted-foreground">
          Assign each login to a client. Only the platform administrator can
          switch between clients.
        </p>
      </div>
      {error && (
        <p role="alert" className="text-red-400">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="text-primary">
          {message}
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {clients.map((c) => (
          <section key={c.id} className="rounded-lg border p-5">
            <h2 className="font-semibold">{c.name}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {
                accounts.filter(
                  (a) => a.client_id === c.id && a.role !== "platform_admin",
                ).length
              }{" "}
              assigned logins ·{" "}
              {c.id === "omaha"
                ? "LPR and crossing review"
                : "Crossing review; data connection pending"}
            </p>
          </section>
        ))}
      </div>
      <section className="rounded-lg border p-5 space-y-4">
        <h2 className="text-lg font-semibold">Create login</h2>
        <p className="text-sm text-muted-foreground">
          Share the temporary password privately. The user must change it before
          viewing data. Viewer can browse and export; Reviewer can also save
          review decisions.
        </p>
        <form
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          onSubmit={(e) =>
            submit(
              e,
              "/api/access/users",
              "POST",
              (d) => Object.fromEntries(d),
              "Login created. Share its temporary password privately.",
            )
          }
        >
          <label>
            Username
            <input
              name="username"
              required
              minLength={3}
              maxLength={80}
              pattern="[a-zA-Z0-9][a-zA-Z0-9_.@\-]{2,79}"
              autoComplete="off"
              className={field}
            />
          </label>
          <label>
            Name
            <input name="full_name" maxLength={100} className={field} />
          </label>
          <label>
            Email (optional)
            <input
              name="email"
              type="email"
              maxLength={254}
              className={field}
            />
          </label>
          <label>
            Client
            <select
              name="client_id"
              defaultValue="virginia-beach"
              className={field}
            >
              {clientOptions}
            </select>
          </label>
          <label>
            Role
            <select name="role" className={field}>
              <option value="viewer">Viewer</option>
              <option value="reviewer">Reviewer</option>
            </select>
          </label>
          <label>
            Temporary password
            <input
              name="password"
              type="password"
              required
              minLength={12}
              maxLength={72}
              autoComplete="new-password"
              className={field}
            />
            <span className="text-xs text-muted-foreground">
              12–72 characters, maximum 72 UTF-8 bytes
            </span>
          </label>
          <Button disabled={busy} type="submit">
            Create login
          </Button>
        </form>
      </section>
      <section className="rounded-lg border p-5 space-y-4">
        <h2 className="text-lg font-semibold">Logins</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b">
                <th className="py-3">User</th>
                <th>Client</th>
                <th>Access</th>
                <th>Status</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id} className="border-b">
                  <td className="py-4 pr-3">
                    <strong>{a.username}</strong>
                    <div className="text-muted-foreground">{a.full_name}</div>
                  </td>
                  <td>
                    {a.role === "platform_admin"
                      ? "All clients"
                      : clients.find((c) => c.id === a.client_id)?.name}
                  </td>
                  <td>
                    {a.role === "platform_admin"
                      ? "Platform admin"
                      : a.role === "reviewer"
                        ? "Reviewer"
                        : "Viewer"}
                  </td>
                  <td>
                    {!a.is_active
                      ? "Disabled"
                      : a.must_change_password
                        ? "Password change required"
                        : "Active"}
                  </td>
                  <td className="text-right">
                    {a.role !== "platform_admin" && (
                      <>
                        <Button
                          variant="ghost"
                          disabled={busy}
                          onClick={() => {
                            setEditing(a);
                            setResetId(null);
                          }}
                        >
                          Edit access
                        </Button>
                        <Button
                          variant="ghost"
                          disabled={busy}
                          onClick={() => {
                            setResetId(a.id);
                            setEditing(null);
                          }}
                        >
                          Reset password
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {editing && (
          <form
            key={editing.id}
            className="rounded border p-4 space-y-3"
            onSubmit={(e) =>
              submit(
                e,
                `/api/access/users/${editing.id}`,
                "PATCH",
                (d) => ({
                  client_id: d.get("client_id"),
                  role: d.get("role"),
                  is_active: d.get("is_active") === "true",
                }),
                "Access updated. Existing sessions have been revoked.",
              )
            }
          >
            <h3 className="font-medium">Edit {editing.username}</h3>
            <p className="text-sm text-muted-foreground">
              Changing access signs this user out on every device.
            </p>
            <label className="block">
              Client
              <select
                name="client_id"
                defaultValue={editing.client_id}
                className={field}
              >
                {clientOptions}
              </select>
            </label>
            <label className="block">
              Role
              <select name="role" defaultValue={editing.role} className={field}>
                <option value="viewer">Viewer</option>
                <option value="reviewer">Reviewer</option>
              </select>
            </label>
            <label className="block">
              Status
              <select
                name="is_active"
                defaultValue={String(Boolean(editing.is_active))}
                className={field}
              >
                <option value="true">Active</option>
                <option value="false">Disabled</option>
              </select>
            </label>
            <Button disabled={busy}>Save access</Button>{" "}
            <Button
              type="button"
              variant="ghost"
              onClick={() => setEditing(null)}
            >
              Cancel
            </Button>
          </form>
        )}
        {resetId !== null && (
          <form
            className="rounded border p-4 space-y-3"
            onSubmit={(e) =>
              submit(
                e,
                `/api/access/users/${resetId}/password`,
                "POST",
                (d) => ({ password: d.get("password") }),
                "Temporary password set. Existing sessions have been revoked.",
              )
            }
          >
            <h3 className="font-medium">
              Reset password for{" "}
              {accounts.find((a) => a.id === resetId)?.username}
            </h3>
            <label className="block">
              New temporary password
              <input
                type="password"
                name="password"
                required
                minLength={12}
                maxLength={72}
                autoComplete="new-password"
                className={field}
              />
            </label>
            <Button disabled={busy}>Set temporary password</Button>{" "}
            <Button
              type="button"
              variant="ghost"
              onClick={() => setResetId(null)}
            >
              Cancel
            </Button>
          </form>
        )}
      </section>
    </div>
  );
}

export function AccountSettings() {
  const { user, logout, accessEnabled } = useAuth();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (!accessEnabled && !user?.must_change_password)
    return <p>Account management is not enabled.</p>;
  return (
    <section className="max-w-lg space-y-4">
      <h1 className="text-2xl font-semibold">Change password</h1>
      <p>
        {user?.must_change_password
          ? "Replace your temporary password to open your client workspace."
          : "Changing your password signs you out on every device."}
      </p>
      {error && (
        <p role="alert" className="text-red-400">
          {error}
        </p>
      )}
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          const data = new FormData(e.currentTarget);
          setError("");
          if (data.get("password") !== data.get("confirm")) {
            setError("Passwords do not match.");
            return;
          }
          setBusy(true);
          try {
            await accessRequest("/api/auth/password", "POST", {
              current_password: data.get("current"),
              password: data.get("password"),
            });
            await logout();
            window.location.assign("/login?password=changed");
          } catch (err) {
            setError((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="block">
          Current password
          <input
            name="current"
            type="password"
            required
            autoComplete="current-password"
            className={field}
          />
        </label>
        <label className="block">
          New password
          <input
            name="password"
            type="password"
            required
            minLength={12}
            maxLength={72}
            autoComplete="new-password"
            className={field}
          />
        </label>
        <label className="block">
          Repeat new password
          <input
            name="confirm"
            type="password"
            required
            minLength={12}
            maxLength={72}
            autoComplete="new-password"
            className={field}
          />
        </label>
        <Button disabled={busy}>Change password & sign out</Button>
      </form>
    </section>
  );
}
